// src\themes\lino\elements\Header\HeaderNavAction.tsx
'use client';

import Link from 'next/link';
import { User, ShoppingBag, LogOut, LayoutDashboard, ShieldCheck } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCartStore } from '@/core/hooks/cart/useCartStore';
import { useAuth } from '@/contexts/authContext';
import { useDictionary } from '@/core/hooks/use-dictionary';
import { HeaderNavSearch } from './HeaderNavSearch';
import { usePathname } from 'next/navigation';

export default function HeaderNavAction() {
  const { cart } = useCartStore();
  const { dictionary } = useDictionary()
  const { state, clearAuth, hasPermission } = useAuth();
  const pathname = usePathname();

  const totalItems = cart?.items?.reduce(
    (sum, item) => sum + (item.quantity || 0),
    0
  );

  const isLoggedIn = state?.isAuthenticated;
  const user = state?.user;

  const loginHref = `/login?returnUrl=${encodeURIComponent(pathname)}`;


  return (
    <div className="flex justify-end items-center gap-5 text-primary-900 group-data-[transparent=true]:text-primary-0">
      <div className="hidden lg:flex items-center">
        {isLoggedIn ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                aria-label="User menu"
                className="flex items-center justify-center gap-1 size-9 rounded-full border border-primary-300 hover:border-primary-900 hover:bg-primary-100 group-data-[transparent=true]:border-primary-0/40 group-data-[transparent=true]:hover:bg-primary-0/15 group-data-[transparent=true]:hover:border-primary-0 transition-colors"
              >
                <User className="w-[16px] h-[16px] stroke-[1.4]" />
              </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              align="center"
              sideOffset={10}
              className="w-40 p-1.5 text-sm shadow-md border border-primary-200 rounded-lg"
            >
              <DropdownMenuLabel className="px-2 py-1.5 text-[13px] text-primary-600 truncate">
                {user?.full_name || 'کاربر'}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="my-1" />

              <DropdownMenuItem
                asChild
                className="px-2 py-1.5 cursor-pointer text-[13px] hover:bg-primary-100 rounded-md"
              >
                <Link href="/profile" className="flex items-center gap-2">
                  {dictionary.forms.profile}
                  <User className="size-4 text-primary-900 stroke-[1.5]" />
                </Link>
              </DropdownMenuItem>

              <DropdownMenuItem
                asChild
                className="px-2 py-1.5 cursor-pointer text-[13px] hover:bg-primary-100 rounded-md"
              >
                <Link href="/dashboard" className="flex items-center gap-2">
                  {dictionary.dashboard.title}
                  <LayoutDashboard className="size-4 text-primary-900 stroke-[1.5]" />
                </Link>
              </DropdownMenuItem>

              {hasPermission("AccessToAdmin") && (
                <DropdownMenuItem
                  asChild
                  className="px-2 py-1.5 cursor-pointer text-[13px] hover:bg-primary-100 rounded-md"
                >
                  <Link href="/admin" className="flex items-center gap-2">
                    {dictionary.ui.profile.adminPanel}
                    <ShieldCheck className="size-4 text-primary-900 stroke-[1.5]" />
                  </Link>
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator className="my-1" />

              <DropdownMenuItem
                onClick={clearAuth}
                className="px-2 py-1.5 cursor-pointer text-[13px] text-red-600 hover:bg-red-50 focus:text-red-600 rounded-md"
              >
                {dictionary.common.logout}
                <LogOut className="size-4 text-red-600 stroke-[1.5]" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Link
            href={loginHref}
            aria-label="User login"
            className="flex items-center justify-center size-9 rounded-full border border-primary-300 hover:border-primary-900 hover:bg-primary-100 group-data-[transparent=true]:border-primary-0/40 group-data-[transparent=true]:hover:bg-primary-0/15 group-data-[transparent=true]:hover:border-primary-0 transition-colors"
          >
            <User className="w-[16px] h-[16px] stroke-[1.4]" />
          </Link>
        )}
      </div>

      <div className="relative">
        <Link
          href="/cart"
          aria-label="Shopping cart"
          className="flex items-center justify-center size-9 rounded-full border border-primary-300 hover:border-primary-900 hover:bg-primary-100 group-data-[transparent=true]:border-primary-0/40 group-data-[transparent=true]:hover:bg-primary-0/15 group-data-[transparent=true]:hover:border-primary-0 transition-colors"
        >
          <ShoppingBag className="w-[17px] h-[17px] stroke-[1.4]" />
        </Link>

        {totalItems > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-accent-blue text-accent-blue-foreground text-[10px] font-medium rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-[3px] leading-none">
            {totalItems}
          </span>
        )}
      </div>
      <div className="hidden lg:flex">
        <HeaderNavSearch />
      </div>
    </div>
  );
}